import React, { type FC, createContext, use, useMemo } from "react";
import { useInRouterContext, useNavigate } from "react-router-dom";
import { toast } from "@heroui/react";

import { AgentRunContext } from "./agentRunContext";
import { productRoute } from "./productRoute";
import type { AgentEntityRef } from "./runner/types";
import { BankingPaths } from "@banks/types/enums";
import { CashRegisterPaths } from "@cash-register/types/enums";
import { AllRecordsContext } from "@domain/context/accountingData/RecordsCtx";
import { BankTransactionsContext } from "@domain/context/BanksContext";
import { CashRegisterTransactionsContext } from "@domain/context/CashRegisterContext";
import { YearPeriodContext } from "@domain/context/CompanyContext";
import { useDmsDocumentsMap } from "@domain/context/DmsDataContexts";
import type { GQL } from "@binale-tech/shared";
import { useTranslation } from "@domain/intl/useTranslation";
import { AppRoutes } from "@domain/routing/routeConstants";
import type { DocumentListFocusState } from "@dms/types/DocumentListFocusState";

/** The year of the run; record links set it before opening a product list. Null outside a run. */
export const AgentRunYearContext = createContext<number | null>(null);

export interface AgentLinks {
    /** The record list of a product for the run's year, full year; does nothing for a product without a list. */
    product(product: GQL.IProductKey): void;
    documents(focusId?: string): void;
    bank(bankId?: string): void;
    kasse(registerId?: string): void;
    contacts(): void;
    rules(): void;
}

/** Links of the chat and the report: they minimize the run (never end it) and navigate in the app. */
export const useAgentLinks = (): AgentLinks => {
    const navigate = useNavigate();
    const { minimize } = use(AgentRunContext);
    const { year: currentYear, period, onChangeYear, onChangePeriod } = use(YearPeriodContext);
    const year = use(AgentRunYearContext);
    const { t } = useTranslation();
    return useMemo(() => {
        const go = (to: string, state?: DocumentListFocusState) => {
            minimize();
            navigate(to, state ? { state } : undefined);
        };
        return {
            product: (product) => {
                const route = productRoute(product);
                if (!route) {
                    return;
                }
                minimize();
                const target = year ?? currentYear;
                if (target !== currentYear || period !== null) {
                    // The record list follows the global year and period; say so once when the link changes them.
                    toast.info(t("app.agent.report.periodChanged", { year: target }));
                }
                if (year !== null) {
                    onChangeYear(year);
                }
                onChangePeriod(null);
                navigate(route);
            },
            documents: (focusId) => go(AppRoutes.dms, focusId ? { focusId } : undefined),
            bank: (bankId) =>
                go(bankId ? `${AppRoutes.banks}/${bankId}/${BankingPaths.TRANSACTIONS}` : AppRoutes.banks),
            kasse: (registerId) =>
                go(
                    registerId
                        ? `${AppRoutes.cashRegisters}/${registerId}/${CashRegisterPaths.TRANSACTIONS}`
                        : AppRoutes.cashRegisters,
                ),
            contacts: () => go(AppRoutes.contacts),
            rules: () => go(AppRoutes.manageAccountRules),
        };
    }, [navigate, minimize, onChangeYear, onChangePeriod, year, currentYear, period, t]);
};

/** Where a detail row leads, read from live data at render; undefined when no page shows the entity. */
const useEntityOpen = (ref: AgentEntityRef): (() => void) | undefined => {
    const links = useAgentLinks();
    const documents = useDmsDocumentsMap();
    const { map: records } = use(AllRecordsContext);
    const { transactionMap: bankTransactions } = use(BankTransactionsContext);
    const { transactionMap: kasseTransactions } = use(CashRegisterTransactionsContext);
    switch (ref.kind) {
        case "document":
            return documents.has(ref.id) ? () => links.documents(ref.id) : undefined;
        case "record": {
            // The live record decides the product; the step's guess is not trusted for a link.
            const product = records.get(ref.id)?.getProductKey();
            return product && productRoute(product) ? () => links.product(product) : undefined;
        }
        case "bankTransaction": {
            const bankId = bankTransactions.get(ref.id)?.bankId;
            return bankId ? () => links.bank(bankId) : undefined;
        }
        case "kasseTransaction": {
            const registerId = kasseTransactions.get(ref.id)?.cashRegisterId;
            return registerId ? () => links.kasse(registerId) : undefined;
        }
        case "contact":
            return undefined;
    }
};

const linkClass = "shrink-0 text-xs text-accent underline underline-offset-2";

const EntityOpenLinkInRouter: FC<{ entity: AgentEntityRef }> = ({ entity }) => {
    const { t } = useTranslation();
    const open = useEntityOpen(entity);
    if (!open) {
        return null;
    }
    return (
        <button type="button" className={linkClass} onClick={open}>
            {t("app.agent.details.open")}
        </button>
    );
};

/** "Öffnen" of a detail row; renders nothing outside a router or when no page shows the entity. */
export const EntityOpenLink: FC<{ entity: AgentEntityRef }> = ({ entity }) =>
    useInRouterContext() ? <EntityOpenLinkInRouter entity={entity} /> : null;
